import { IchiInsights } from 'common/models/Insights';
import { Nullable } from 'common/models/optional';
import { ichi } from 'constants/abis/ichi';
import { ichiV2 } from 'constants/abis/ichiV2';
import { ChainId } from 'constants/constants';
import {
  IchiContract,
  isIchiContract,
  isIchiV2Contract,
} from 'constants/contracts';
import isError from 'lodash/isError';
import { NumberUtils } from 'utils/numberUtils';
import { StorageUtils } from 'utils/storageUtils';
import { Address } from 'viem';
import { IchiSwapEvent } from 'xstate/ichiSwapEvents';
import { getPublicClient, writeContract, readContract } from '@wagmi/core';

export namespace IchiSwapApi {
  export type DataResponse = {
    allowanceBN: bigint;
    isApproved: boolean;
  };

  type DataArgs = {
    amount: string;
    chainId: ChainId;
    accountAddress: Address;
    legacyIchiContainer?: IchiContract<typeof ichi>;
    ichiV2Container?: IchiContract<typeof ichiV2>;
  };

  type ApproveArgs = {
    chainId: ChainId;
    accountAddress: Address;
    legacyIchiContainer?: IchiContract<typeof ichi>;
    ichiV2Container?: IchiContract<typeof ichiV2>;
  };

  type ConvertArgs = {
    amount: string;
    accountAddress: Address;
    chainId: number;
    legacyIchiContainer?: IchiContract<typeof ichi>;
    ichiV2Container?: IchiContract<typeof ichiV2>;
  };

  export interface IIchiSwap {
    getData(args: DataArgs): Promise<DataResponse>;
    approveConversion(
      args: ApproveArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
    resetAllowance(args: ApproveArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    convertIchiToIchiV2(
      args: ConvertArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
    // convertIchiV2ToLegacy(args: ConvertArgs): Promise<Nullable<StorageUtils.StoredTx>>;
  }

  export class IchiSwap implements IIchiSwap {
    private static instance: IIchiSwap;

    static maxApproval = 2n ** 256n - 1n;

    static maxApprovalString = IchiSwap.maxApproval.toString();

    static approveConversionFN = 'approveTokenConversion';

    static resetAllowanceFN = 'resetAllowance ';

    static convertIchiToIchiV2FN = 'convertIchiToIchiV2';

    static getDataFN: 'getData';

    private constructor() {}

    public static getInstance(): IIchiSwap {
      if (!IchiSwap.instance) {
        IchiSwap.instance = new IchiSwap();
      }
      return IchiSwap.instance;
    }

    async approveConversion(
      args: ApproveArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const functionName = IchiSwap.approveConversionFN;

      if (args.legacyIchiContainer == null) {
        const message =
          'Unable to approve, could not resolve the legacy ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      if (args.ichiV2Container == null) {
        const message =
          'Unable to approve, could not resolve the current ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      try {
        if (
          isIchiContract(args.legacyIchiContainer.token, {
            container: args.legacyIchiContainer,
            ...args,
          })
        ) {
          const publicClient = getPublicClient();
          const gasLimit = await publicClient.estimateContractGas({
            address: args.legacyIchiContainer.token.address,
            abi: args.legacyIchiContainer.abi,
            functionName: 'approve',
            account: args.accountAddress,
            args: [args.ichiV2Container.token.address, IchiSwap.maxApproval],
          });

          const tx = await writeContract({
            address: args.legacyIchiContainer.token.address,
            abi: args.legacyIchiContainer.abi,
            functionName: 'approve',
            args: [args.ichiV2Container.token.address, IchiSwap.maxApproval],
            gas: gasLimit,
          });

          // Storage the tx hash in local storage
          storedTx = await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Approving...',
              success: 'Success!',
              error: 'Error!',
            },
            value: IchiSwap.maxApproval,
            ...args,
          });

          IchiInsights.trackEvent({
            event: IchiSwapEvent.APPROVE,
            properties: {
              ...args,
              amount: IchiSwap.maxApprovalString,
              handledAt: `ichiSwapApi:${functionName}`,
              txHash: tx.hash,
            },
          });
        } else {
          throw new Error('Could not approve, could not resolve contract.');
        }
      } catch (error) {
        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    async resetAllowance(
      args: ApproveArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const functionName = IchiSwap.resetAllowanceFN;

      if (args.legacyIchiContainer == null) {
        const message =
          'Unable to reset allowance, could not resolve the legacy ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      if (args.ichiV2Container == null) {
        const message =
          'Unable to reset allowance, could not resolve the current ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      try {
        if (
          isIchiContract(args.legacyIchiContainer.token, {
            container: args.legacyIchiContainer,
            ...args,
          })
        ) {
          const publicClient = getPublicClient();
          const gasLimit = await publicClient.estimateContractGas({
            address: args.legacyIchiContainer.token.address,
            abi: args.legacyIchiContainer.abi,
            functionName: 'approve',
            account: args.accountAddress,
            args: [args.ichiV2Container.token.address, 0n],
          });

          const tx = await writeContract({
            address: args.legacyIchiContainer.token.address,
            abi: args.legacyIchiContainer.abi,
            functionName: 'approve',
            args: [args.ichiV2Container.token.address, 0n],
            gas: gasLimit,
          });

          // Storage the tx hash in local storage
          storedTx = await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Resetting allowance...',
              success: 'Success!',
              error: 'Error!',
            },
            value: IchiSwap.maxApproval,
            ...args,
          });

          IchiInsights.trackEvent({
            event: IchiSwapEvent.RESET_ALLOWANCE,
            properties: {
              ...args,
              amount: '0',
              handledAt: `ichiSwapApi:${functionName}`,
              txHash: tx.hash,
            },
          });
        } else {
          throw new Error(
            'Could not reset allowance, could not resolve contract.'
          );
        }
      } catch (error) {
        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    async convertIchiToIchiV2(
      args: ConvertArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const stringBN = NumberUtils.stringToBN(args.amount, 9n);

      const functionName = IchiSwap.convertIchiToIchiV2FN;

      if (args.legacyIchiContainer == null) {
        const message =
          'Unable to upgrade, could not resolve the legacy ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      if (args.ichiV2Container == null) {
        const message =
          'Unable to upgrade, could not resolve the current ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      try {
        if (
          isIchiV2Contract(args.ichiV2Container.token, {
            container: args.ichiV2Container,
            ...args,
          })
        ) {
          const publicClient = getPublicClient();
          const gasLimit = await publicClient.estimateContractGas({
            address: args.ichiV2Container.token.address,
            abi: args.ichiV2Container.abi,
            functionName: 'convertToV2',
            account: args.accountAddress,
            args: [stringBN],
          });

          const tx = await writeContract({
            address: args.ichiV2Container.token.address,
            abi: args.ichiV2Container.abi,
            functionName: 'convertToV2',
            args: [stringBN],
            gas: gasLimit,
          });

          // Storage the tx hash in local storage
          storedTx = await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: `Upgrading ${args.amount} legacy ICHI...`,
              success: 'Success!',
              error: 'Error!',
            },
            value: args.amount,
            ...args,
          });

          IchiInsights.trackEvent({
            event: IchiSwapEvent.UPGRADE_ICHI,
            properties: {
              ...args,
              amount: args.amount,
              handledAt: `ichiSwapApi:${functionName}`,
              txHash: tx.hash,
            },
          });
        } else {
          throw new Error('Could not upgrade, could not resolve contract.');
        }
      } catch (error) {
        console.error(error);
        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    async getData(args: DataArgs): Promise<DataResponse> {
      const functionName = IchiSwap.getDataFN;

      if (args.legacyIchiContainer == null) {
        const message =
          'Unable to downgrade, could not resolve the legacy ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      if (args.ichiV2Container == null || args.ichiV2Container.token == null) {
        const message =
          'Unable to downgrade, could not resolve the current ichi contract';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `ichiSwapApi:${functionName}`,
          },
        });
        throw new Error(message);
      }

      let allowanceBN = 0n;
      let isApproved = false;

      try {
        if (
          isIchiContract(args.legacyIchiContainer.token, {
            container: args.legacyIchiContainer,
            ...args,
          })
        ) {
          allowanceBN = args.accountAddress
            ? await readContract({
                address: args.legacyIchiContainer.token.address,
                abi: args.legacyIchiContainer.abi,
                functionName: 'allowance',
                args: [args.accountAddress, args.ichiV2Container.token.address],
                chainId: args.chainId,
              })
            : 0n;

          isApproved =
            allowanceBN > 0n &&
            allowanceBN >=
              BigInt(NumberUtils.getBNString18(Number(args.amount)));
        }
      } catch (error) {
        if (isError(error)) {
          IchiInsights.trackException({
            error,
            properties: {
              ...args,
              handledAt: 'ichiSwapApi:getData',
            },
          });
          console.error('Could not get ichi swap data for', error);
        }
      }

      const result: DataResponse = {
        allowanceBN,
        isApproved,
      };
      console.debug('[ichiSwapApi.getData]', result);
      return result;
    }
  }
}
